import React from "react"
import autobind from "class-autobind"
import { Toggle, SelectField, MenuItem, CircularProgress } from "material-ui"
import { IDate, IUserDocument, IScheduleDocument, IUserFilterDocument, EDefaultFilterValues, IResourcePlannerScheduleFilter, ISelectedFilterTemplateId, ISchedulePatternTemplate, IEmployeeTypeData } from "../../../../models"
import * as scheduleHelper from "../../../../helpers/scheduleHelper"
import strings from "../../../../localization/strings"
import ScheduleResourcePlannerWeekControl from "./daily/ScheduleResourcePlannerWeekControl"
import ScheduleResourcePlannerItem from "./daily/ScheduleResourcePlannerItem"
import ScheduleResourcePlannerWeeklyItem from "./weekly/ScheduleResourcePlannerWeeklyItem"
import ScheduleResourcePlannerWeeklyItemCollapsed from "./weekly/ScheduleResourcePlannerWeeklyItemCollapsed"
import ResourcePlannerScheduleFilter from "../../../filter_template/resource_planner_schedule/ResourcePlannerScheduleFilter"
import * as api from "../../../../api"
import AuthenticationStore from "../../../../stores/AuthenticationStore"
import AppStateStore from "../../../../stores/AppStateStore"
import UnAssignEmployeeDialog from "../../assign_employee/UnAssignEmployeeDialog"
import _ from "lodash"
import AssignEmployeeDrawer from "../../assign_employee/AssignEmployeeDrawer"

interface IProps {
	readonly: boolean
	postedSchedules: IScheduleDocument[]
	reload(): void
}
interface IState {
	loading: boolean
	selectedFilterTemplateId: ISelectedFilterTemplateId
	filterTemplates: IResourcePlannerScheduleFilter[]
	assignedEmployeeTypeData: IEmployeeTypeData[]
	selectedDay: IDate
	collapseSchedule: boolean
	viewByEmployee: boolean
	viewType: string
	assignPatternTemplate: ISchedulePatternTemplate
	unAssignPatternTemplate: ISchedulePatternTemplate
}

export default class ScheduleResourcePlanner extends React.Component<IProps, IState> {
	constructor(props: IProps) {
		super(props)
		this.state = {
			loading: false,
			selectedFilterTemplateId: EDefaultFilterValues.NONE,
			filterTemplates: [],
			assignedEmployeeTypeData: [],
			selectedDay: scheduleHelper.getStartDate(this.props.postedSchedules),
			collapseSchedule: false,
			viewByEmployee: false,
			viewType: "daily",
			assignPatternTemplate: null,
			unAssignPatternTemplate: null
		}
		autobind(this)
	}

	componentDidMount() {
		this.getFilterData(this.props)
	}

	componentWillReceiveProps(nextProps: IProps) {
		const propsEmployeeIds = this.getEmployeeIds(this.props.postedSchedules)
		const nextPropsEmployeeIds = this.getEmployeeIds(nextProps.postedSchedules)
		nextPropsEmployeeIds.find(i => !propsEmployeeIds.includes(i)) &&
			this.getEmployeeTypeData(nextPropsEmployeeIds)
	}

	getFilterData(props: IProps) {
		const employeeIds = this.getEmployeeIds(props.postedSchedules)
		this.setState({ loading: true }, async () => {
			await Promise.all([this.getFilterTemplates(), this.getEmployeeTypeData(employeeIds)])
			this.setState({ loading: false })
		})
	}

	getFilterTemplates() {
		return new Promise((resolve, reject) => {
			api.getFilters(AuthenticationStore.getLoggedInUser().Id).then(res => {
				const result = res.data as IUserFilterDocument
				const selectedFilterTemplateId = result && result.SelectedResourcePlannerScheduleFilter ? result.SelectedResourcePlannerScheduleFilter : EDefaultFilterValues.NONE
				const filterTemplates = result && result.ResourcePlannerScheduleFilters ? result.ResourcePlannerScheduleFilters : []
				this.setState({ filterTemplates, selectedFilterTemplateId }, () =>
					resolve(true)
				)
			})
		})
	}

	getEmployeeTypeData(employeeIds: string[]) {
		return new Promise((resolve, reject) => {
			api.getEmployeeTypeData(employeeIds).then(res =>
				this.setState({ assignedEmployeeTypeData: res.data }, () =>
					resolve(true)
				)
			)
		})
	}

	getEmployeeIds(schedules: IScheduleDocument[]) {
		const employeeIds = []
		schedules.forEach(schedule =>
			schedule.Schedules.forEach(pt =>
				pt.AssignedUserId && !employeeIds.includes(pt.AssignedUserId) &&
				employeeIds.push(pt.AssignedUserId)
			)
		)
		return employeeIds
	}

	handleAssignEmployee(assignPatternTemplate: ISchedulePatternTemplate) {
		this.setState({ assignPatternTemplate })
	}

	handleUnAssignEmployee(unAssignPatternTemplate: ISchedulePatternTemplate) {
		this.setState({ unAssignPatternTemplate })
	}

	handleUnAssign(unAssignPatternTemplates: ISchedulePatternTemplate[]) {

		this.setState({ unAssignPatternTemplate: null })
		if (unAssignPatternTemplates.length === 0)
			return

		const unAssignSchedule = this.props.postedSchedules.find(i => i.Schedules.find(e => e.Id === unAssignPatternTemplates[0].Id) ? true : false)
		const schedule = scheduleHelper.unAssignEmployeeForSchedule(unAssignSchedule, unAssignPatternTemplates)

		api.updateSchedule(schedule).then(res => {
			AppStateStore.setProgressSnackbarContent(strings.Shift_unassigned_successfully)
			this.props.reload()
		})
	}

	render() {
		if (this.state.loading)
			return <div className="spinner"><CircularProgress /></div>

		const selectedFilterTemplate = this.state.filterTemplates.find(i => i.Id === this.state.selectedFilterTemplateId)
		const filteredPostSchedules = scheduleHelper.getSchedulesByFilterTemplate(this.props.postedSchedules, selectedFilterTemplate, this.state.assignedEmployeeTypeData)
		return (
			<div>
				<div style={{ display: "flex", alignItems: "flex-start", marginLeft: 20, marginTop: -20, marginBottom: 20 }}>
					<ResourcePlannerScheduleFilter
						filterTemplates={this.state.filterTemplates}
						selectedFilterTemplateId={this.state.selectedFilterTemplateId}
						handleChangeFilterTemplates={filterTemplates => this.setState({ filterTemplates })}
						handleSelectFilterTemlate={filterId =>
							this.setState({ selectedFilterTemplateId: filterId }, () =>
								api.setSelectedResourcePlannerScheduleFilter(AuthenticationStore.getLoggedInUser().Id,
									(filterId === EDefaultFilterValues.NONE || filterId === EDefaultFilterValues.CREATE_NEW) ? "" : filterId as string
								)
							)
						}
					/>
					<div style={{ display: "grid", marginLeft: 25 }}>
						<div style={{ gridColumn: 1 }}>
							<SelectField
								floatingLabelText={strings.View_Type}
								value={this.state.viewType}
								onChange={(_e, _key, viewType) => this.setState({ viewType })}
								style={{ width: 250 }}
							>
								<MenuItem value={"daily"} primaryText={"Daily View"} />
								<MenuItem value={"weekly"} primaryText={"Weekly View"} />
							</SelectField>
						</div>
						{this.state.viewType == "weekly" &&
							<div style={{ gridColumn: 1, display: "grid", gridTemplateColumns: "min-content min-content" }}>
								<Toggle
									data-intercom-target={`menu-AccountSettings-Mobile-Toggle-collapseSchedule`}
									toggled={this.state.collapseSchedule}
									onToggle={() => { this.setState({ collapseSchedule: !this.state.collapseSchedule }) }}
									labelPosition="left"
									label={"Collapse All Schedules"}
									style={{ width: 300, gridColumn: 1 }}
								/>
								{this.state.collapseSchedule &&
									<Toggle
										data-intercom-target={`menu-AccountSettings-Mobile-Toggle-viewByEmployee`}
										toggled={this.state.viewByEmployee}
										onToggle={() => { this.setState({ viewByEmployee: !this.state.viewByEmployee }) }}
										labelPosition="left"
										label={"View By Employee"}
										style={{ width: 300, gridColumn: 2, marginLeft: 20 }}
									/>
								}
							</div>
						}
					</div>
				</div>
				{this.state.viewType == "daily" ?
					<div>
						<ScheduleResourcePlannerWeekControl
							schedules={filteredPostSchedules}
							dayLength={scheduleHelper.durationInDays(filteredPostSchedules)}
							startDate={scheduleHelper.getStartDate(filteredPostSchedules)}
							onDayChange={day => this.setState({ selectedDay: day })}
						/>
						<div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", marginTop: 10 }} >
							{filteredPostSchedules.map(schedule => /*.filter(
								schedule => schedule.Schedules.filter(
									s => DateUtils.isSameDay(moment(s.ShiftTemplates[0].StartDateTime).toDate(), moment(this.state.selectedDay.toString()).toDate())).length > 0)*/
								<ScheduleResourcePlannerItem
									key={schedule.Id}
									schedule={schedule}
									selectedDay={this.state.selectedDay}
									handleAssignEmployee={this.handleAssignEmployee}
									handleUnAssignEmployee={this.handleUnAssignEmployee}
									reload={this.props.reload}
								/>
							)}
						</div>
					</div> :
					this.state.collapseSchedule ?
						<ScheduleResourcePlannerWeeklyItemCollapsed
							schedules={filteredPostSchedules}
							viewByEmployee={this.state.viewByEmployee}
							handleAssignEmployee={this.handleAssignEmployee}
							handleUnAssignEmployee={this.handleUnAssignEmployee}
						/> :
						<div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", marginTop: 10 }} >
							{filteredPostSchedules.map(schedule =>
								<ScheduleResourcePlannerWeeklyItem
									key={schedule.Id}
									schedule={schedule}
									handleAssignEmployee={this.handleAssignEmployee}
									handleUnAssignEmployee={this.handleUnAssignEmployee}
								/>
							)}
						</div>
				}
				{this.state.assignPatternTemplate &&
					<AssignEmployeeDrawer
						open={!!this.state.assignPatternTemplate}
						schedule={this.props.postedSchedules.find(i => i.Schedules.find(e => e.Id === this.state.assignPatternTemplate.Id) ? true : false)}
						assignPatternTemplate={this.state.assignPatternTemplate}
						handleClose={() => this.setState({ assignPatternTemplate: null })}
						handleAssign={(shiftIds: string[], assignEmployeeId: string) => {
							this.setState({ assignPatternTemplate: null })
							api.assignShift(assignEmployeeId, shiftIds).then(() => {
								AppStateStore.setProgressSnackbarContent(strings.Shift_assigned_successfully)
								this.props.reload()
							})
						}}
					/>
				}
				{this.state.unAssignPatternTemplate &&
					<UnAssignEmployeeDialog
						schedule={this.props.postedSchedules.find(i => i.Schedules.find(e => e.Id === this.state.unAssignPatternTemplate.Id) ? true : false)}
						unAssignPatternTemplate={this.state.unAssignPatternTemplate}
						handleClose={() => this.setState({ unAssignPatternTemplate: null })}
						handleUnAssign={this.handleUnAssign}
					/>
				}
			</div>
		)
	}
}
